import React, { FunctionComponent } from 'react';
import { useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Box } from '@rebass/emotion';

import { AppTheme } from '@app/styles/theme';
import ErrorBoundary from '@app/components/ErrorBoundary';
import Link from '@app/components/Link';
import Loader from '@app/components/Loader';
import { H1, H2 } from '@app/shop/components/typography';
import { ShopStoreProvider } from '@app/shop/context/ShopStoreContext';
import { getButtonStyles } from '@app/shop/components/Button';
import useShopStore from '@app/shop/hooks/useShopStore';
import useGTMTracking from '@app/shop/hooks/useGTMTracking';

import {
  OrderHistoryDetailProvider,
  useOrderHistoryDetail,
} from './OrderHistoryDetailContext';
import Summary from './Summary';
import ItemsDetail from './ItemsDetail';
import PaymentDetail from './PaymentDetail';
import messages from './OrderHistoryDetail.messages';

const SUPPORT_URL =
  'https://support.native-instruments.com/hc/en-us/categories/360000053657';

const OrderHistoryDetailContent: FunctionComponent = () => {
  const {
    itemsData,
    itemsQueryState,
    paymentQueryState,
  } = useOrderHistoryDetail();

  useGTMTracking({});

  if (itemsQueryState.loading || paymentQueryState.loading) {
    return <Loader />;
  }

  return (
    <>
      <Box mb={20}>
        <H1>
          <FormattedMessage {...messages.title} />
        </H1>
        <H2>
          <FormattedMessage {...messages.subTitle} /> #{itemsData?.number}
        </H2>
        <Summary />
      </Box>
      <a
        href={SUPPORT_URL}
        css={(theme: AppTheme) => getButtonStyles({ type: 'primary', theme })}
      >
        <FormattedMessage {...messages.supportButton} />
      </a>

      <Box mb={40} mt={40}>
        <ItemsDetail />
      </Box>

      <Box mb={40}>
        <PaymentDetail />
      </Box>

      <Link
        to="/:language/account/orders/"
        css={(theme: AppTheme) => getButtonStyles({ type: 'secondary', theme })}
      >
        <FormattedMessage {...messages.backButton} />
      </Link>
    </>
  );
};

const OrderHistoryDetailWrapper: FunctionComponent = () => {
  /*
  Translated strings from Magento2 (eg country name, status) are localised according to the current Store code header, so we need to ensure it is set before proceeding
  */
  const { storeCode } = useShopStore();
  const { orderNumber } = useParams<{ orderNumber?: string }>();

  if (!(orderNumber && storeCode)) return null;

  return (
    <OrderHistoryDetailProvider orderNumber={orderNumber}>
      <OrderHistoryDetailContent />
    </OrderHistoryDetailProvider>
  );
};

const OrderHistoryDetail: FunctionComponent = () => (
  <ErrorBoundary>
    <ShopStoreProvider>
      <OrderHistoryDetailWrapper />
    </ShopStoreProvider>
  </ErrorBoundary>
);

export default OrderHistoryDetail;
